/**
 * @file    useEnterpriseTabCleanup.ts
 * @scope   ENTERPRISE
 * @summary Side effects that must run when the TabNavigator pops Enterprise sub-views.
 * @consumes EnterpriseContext, EnterpriseNavigationContext, DiyEnterprise tab descriptors
 * @produces `onNavigate` (post-navigation cleanup) and `maxCrumbs` (breadcrumb depth)
 * @sideEffects Strips incomplete fields from the Enterprise context and clears the drawer's
 *              sub-view contexts.
 * @notes Keeps all Enterprise tab knowledge out of DrawerHeader, which stays world-agnostic.
 */
import { FieldsManagerTab } from 'components/DiyWidgetEngine/Subtypes/Enterprise/Views/FieldsManager/FieldsManager';
import { CalculationsSettingsManagerTab } from 'components/DiyWidgetEngine/Subtypes/Enterprise/Views/CalculationsSettingsManager/CalculationsSettingsManager';
import { FiltersSettingsManagerTab } from 'components/DiyWidgetEngine/Subtypes/Enterprise/Views/FiltersSettingsManager/FiltersSettingsManager';
import { PartitionFiltersManagerTab } from 'components/DiyWidgetEngine/Subtypes/Enterprise/Views/PartitionFiltersManager/PartitionFiltersManager';
import { useEnterpriseContext } from 'components/DiyWidgetEngine/Subtypes/Enterprise/State/EnterpriseContext';
import { useEnterpriseNavigation } from '../plugins/enterprise/EnterpriseNavigationContext';
import { EngineTabCleanup, EngineTabCleanupParams } from '../plugins/types';

// ⚡ ENTERPRISE V2 — NEW WORLD. Do not use for legacy charts.

export function useEnterpriseTabCleanup({
  currentTab,
  selectedPartition,
}: EngineTabCleanupParams): EngineTabCleanup {
  const { stripIncompleteFields } = useEnterpriseContext();
  const { setAdvancedSettingsContext, setFiltersSettingsContext, setPartitionFiltersContext } =
    useEnterpriseNavigation();

  /**
   * Drops fields the user left half-configured when the FieldsManager tab is popped.
   *
   * WHY dispatch instead of computing here: stripping via a read-compute-write from the component
   * is a full-partition replace that can be clobbered by an interleaved async writer — which is
   * what made incomplete fields resurrect on tab-pop. The reducer computes the strip atomically
   * against the freshest state instead.
   */
  const stripIncompleteFieldsOnPop = (poppedTabs: any[]) => {
    const poppedFieldsManager = poppedTabs.some((t) => t.name === FieldsManagerTab.name);
    if (!poppedFieldsManager || !selectedPartition?.value) return;
    stripIncompleteFields(selectedPartition.value);
  };

  /**
   * Clears a sub-view context unless we are navigating *back* into FieldsManager from that very
   * sub-view — in which case the context must survive so the correct field stays expanded.
   */
  const clearContextUnlessReturning = (
    tab: any,
    poppedTabs: any[],
    sourceTabName: string,
    clear: () => void
  ) => {
    const isReturning =
      tab.name === FieldsManagerTab.name && poppedTabs.some((t) => t.name === sourceTabName);
    if (!isReturning) clear();
  };

  const onNavigate = (tab: any, poppedTabs: any[]) => {
    stripIncompleteFieldsOnPop(poppedTabs);
    clearContextUnlessReturning(tab, poppedTabs, CalculationsSettingsManagerTab.name, () =>
      setAdvancedSettingsContext(null)
    );
    clearContextUnlessReturning(tab, poppedTabs, FiltersSettingsManagerTab.name, () =>
      setFiltersSettingsContext(null)
    );
    clearContextUnlessReturning(tab, poppedTabs, PartitionFiltersManagerTab.name, () =>
      setPartitionFiltersContext(null)
    );
  };

  // WHY: the third-level settings screens show "Main > Fields" as breadcrumbs, every other tab
  // shows a single crumb.
  const maxCrumbs =
    currentTab.name === CalculationsSettingsManagerTab.name ||
    currentTab.name === FiltersSettingsManagerTab.name ||
    currentTab.name === PartitionFiltersManagerTab.name
      ? 2
      : 1;

  return { onNavigate, maxCrumbs };
}

export default useEnterpriseTabCleanup;
